// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link, useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, MoreHorizontal, Pencil, RotateCcw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/api/client";
import {
  type ChainSettingEntry,
  useAddChainSettingMutation,
  useChainSettingsQuery,
  useDeleteChainSettingMutation,
  useEditChainSettingMutation,
} from "@/api/hooks/use-chains";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { GroupBadge } from "@/components/shared/group-badge";
import { KeyChip } from "@/components/shared/key-chip";
import { RowId } from "@/components/shared/row-id";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FieldTypeBadge } from "@/features/settings/field-type-badge";
import { RestoreDefaultDialog } from "@/features/settings/restore-default-dialog";
import { SettingDialog } from "@/features/settings/setting-dialog";
import { SettingValueCell } from "@/features/settings/setting-value-cell";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { cn } from "@/lib/utils";

interface ChainSettingsTabProps {
  chainName: string;
  initialFilters?: string;
}

const FILTER_DEFAULTS = {
  search: "",
  group: "",
  onlyOverrides: true,
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "settingId",
  sortDesc: false,
};

export function ChainSettingsTab({ chainName, initialFilters }: ChainSettingsTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.chainsSettingsEdit);
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  const initial = useMemo(() => decodeFilters(initialFilters, FILTER_DEFAULTS), [initialFilters]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>(
    initial.sortId !== "settingId" || initial.sortDesc ? [{ id: initial.sortId, desc: initial.sortDesc }] : [],
  );
  const [search, setSearch] = useState(initial.search);
  const [onlyOverrides, setOnlyOverrides] = useState(initial.onlyOverrides);
  const [group, setGroup] = useState(initial.group);

  const groups = useMemo(() => [...(context?.storeSettingsGroups ?? [])].sort(), [context]);

  const isFiltered = !!(search || group || !onlyOverrides);

  const [editSetting, setEditSetting] = useState<ChainSettingEntry | null>(null);
  const [restoreSetting, setRestoreSetting] = useState<ChainSettingEntry | null>(null);

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setGroup("");
    setOnlyOverrides(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters, disabled: editSetting !== null || restoreSetting !== null });

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const current = {
        search,
        group,
        onlyOverrides,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortId: sorting[0]?.id ?? "settingId",
        sortDesc: sorting[0]?.desc ?? false,
        ...overrides,
      };
      const encoded = encodeFilters(current, FILTER_DEFAULTS);
      navigate({
        to: "/chains/$chainName",
        params: { chainName },
        search: { t: "settings", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [search, group, onlyOverrides, pagination.pageIndex, pagination.pageSize, sorting, navigate, chainName],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const sortBy = (sorting[0]?.id ?? "settingId") as
    | "settingId"
    | "name"
    | "key"
    | "description"
    | "groupName"
    | "defaultValue"
    | "overrideId"
    | "overrideValue"
    | "storeOverridesCount";
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const { data, isLoading, refetch, isFetching } = useChainSettingsQuery({
    chainName,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    group: group || undefined,
    onlyOverrides: onlyOverrides || undefined,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/chains/{chain_name}/settings", {
      params: {
        path: { chain_name: chainName },
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[group]": group || undefined,
          "filter[onlyOverrides]": onlyOverrides || undefined,
        },
      },
    });
    const items = (res.data as unknown as { settings: ChainSettingEntry[] }).settings;
    downloadCsv(
      `chain-${chainName}-settings.csv`,
      ["ID", "Key", "Name", "Group", "Type", "Default Value", "Override Value", "Store Overrides"],
      items.map((s) => [
        String(s.settingId),
        s.key,
        s.name,
        s.groupName,
        s.fieldType,
        s.defaultValue ?? "",
        s.overrideValue ?? "",
        String(s.storeOverridesCount),
      ]),
    );
  }, [chainName, search, sortBy, orderBy, group, onlyOverrides]);

  const addSetting = useAddChainSettingMutation(chainName);
  const editSettingMutation = useEditChainSettingMutation(chainName);
  const deleteSetting = useDeleteChainSettingMutation(chainName);

  async function handleEditSave(value: string) {
    if (!editSetting) return;
    try {
      if (editSetting.overrideId) {
        await editSettingMutation.mutateAsync({
          overrideId: editSetting.overrideId,
          newValue: value,
        });
      } else {
        await addSetting.mutateAsync({
          settingId: editSetting.settingId,
          newValue: value,
        });
      }
      toast.success(t("settings.list.toast.updated", { name: editSetting.name }));
    } catch {
      // Error toast handled by API middleware
    }
  }

  async function handleRestore() {
    if (!restoreSetting?.overrideId) return;
    try {
      await deleteSetting.mutateAsync(restoreSetting.overrideId);
      toast.success(t("settings.list.toast.restored", { name: restoreSetting.name }));
      setRestoreSetting(null);
    } catch {
      // Error toast handled by API middleware
    }
  }

  const columns = useMemo<DataTableColumnDef<ChainSettingEntry>[]>(
    () => [
      {
        accessorKey: "settingId",
        header: t("settings.list.columns.id"),
        size: 80,
        enableHiding: false,
        meta: { columnLabel: t("settings.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.settingId} />,
      },
      {
        accessorKey: "name",
        header: t("settings.list.columns.name"),
        meta: { columnLabel: t("settings.list.columns.name") },
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <Link
              to="/settings/$settingName"
              params={{ settingName: row.original.key }}
              className="font-medium text-primary hover:underline"
            >
              {row.original.name}
            </Link>
            <KeyChip label={row.original.key} />
          </div>
        ),
      },
      {
        accessorKey: "groupName",
        header: t("settings.list.columns.groupName"),
        meta: { columnLabel: t("settings.list.columns.groupName") },
        cell: ({ row }) => <GroupBadge name={row.original.groupName} />,
      },
      {
        accessorKey: "fieldType",
        header: t("settings.list.columns.fieldType"),
        size: 90,
        enableSorting: false,
        meta: { columnLabel: t("settings.list.columns.fieldType"), forceWidth: true },
        cell: ({ row }) => <FieldTypeBadge type={row.original.fieldType} />,
      },
      {
        id: "value",
        header: t("settings.list.columns.value"),
        enableSorting: false,
        meta: { columnLabel: t("settings.list.columns.value") },
        cell: ({ row }) => (
          <SettingValueCell
            value={row.original.overrideValue ?? row.original.defaultValue}
            fieldType={row.original.fieldType}
            name={row.original.name}
          />
        ),
      },
      {
        id: "status",
        header: t("settings.list.columns.status"),
        enableSorting: false,
        size: 100,
        meta: { columnLabel: t("settings.list.columns.status") },
        cell: ({ row }) =>
          row.original.overrideId ? (
            <Badge
              variant="secondary"
              className={cn(
                "font-normal bg-[#E3F1FF] border-blue-200 text-foreground dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900",
              )}
            >
              Override
            </Badge>
          ) : (
            <Badge variant="secondary" className="font-normal">
              Default
            </Badge>
          ),
      },
      {
        accessorKey: "storeOverridesCount",
        header: t("settings.list.columns.storeOverrides"),
        size: 120,
        meta: { columnLabel: t("settings.list.columns.storeOverrides") },
        cell: ({ row }) =>
          row.original.storeOverridesCount > 0 ? (
            <Link
              to="/settings/$settingName"
              params={{ settingName: row.original.key }}
              search={{ t: "stores" }}
              className="text-primary hover:underline"
            >
              {row.original.storeOverridesCount}
            </Link>
          ) : (
            <span className="text-muted-foreground">0</span>
          ),
      },
      {
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) =>
          canEdit ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
                <DropdownMenuItem onSelect={() => setEditSetting(row.original)}>
                  <Pencil className="h-4 w-4" />
                  {t("settings.list.actions.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setRestoreSetting(row.original)} disabled={!row.original.overrideId}>
                  <RotateCcw className="h-4 w-4" />
                  {t("settings.list.actions.restoreDefault")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null,
      },
    ],
    [t, canEdit],
  );

  return (
    <div className="p-6">
      <DataTable
        storageKey="chain-settings"
        columns={columns}
        data={data?.settings ?? []}
        totalCount={data?.count ?? 0}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        search={search}
        onSearchChange={setSearch}
        searchDebounceMs={1000}
        onRefresh={refetch}
        isRefreshing={isFetching}
        onExport={handleExport}
        searchPlaceholder={t("settings.list.searchPlaceholder")}
        filters={
          <>
            <FilterSelect
              label={t("settings.list.filter.group")}
              value={group}
              onChange={(v) => {
                setGroup(v);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              options={groups.map((g) => ({ label: g, value: g }))}
              searchPlaceholder={t("settings.list.filter.searchGroups")}
              emptyText={t("settings.list.filter.noGroups")}
              loading={contextLoading}
            />
            <FilterToggle
              active={onlyOverrides}
              onChange={() => {
                setOnlyOverrides((v) => !v);
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
              icon={Filter}
              label={t("settings.list.filter.overridesOnly")}
            />
            {isFiltered && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={clearFilters}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        }
      />

      <SettingDialog
        open={!!editSetting}
        onClose={() => setEditSetting(null)}
        settingName={editSetting?.name ?? ""}
        settingKey={editSetting?.key ?? ""}
        description={editSetting?.description}
        fieldType={editSetting?.fieldType ?? "string"}
        enumValues={editSetting?.enumValues}
        defaultValue={editSetting?.defaultValue ?? ""}
        currentValue={editSetting?.overrideValue ?? editSetting?.defaultValue}
        onSave={handleEditSave}
      />

      <RestoreDefaultDialog
        open={!!restoreSetting}
        onClose={() => setRestoreSetting(null)}
        onConfirm={handleRestore}
        settingName={restoreSetting?.name}
        confirmDisabled={!canEdit}
      />
    </div>
  );
}
